import React, { memo, useMemo } from 'react';
import { Heart, Package, TrendingDown, ShieldCheck, Truck } from 'lucide-react';
import { getSellerTrustLabel } from '../utils/sellerTrust';
import { formatRelativePostedTime } from '../utils/dataConsistency';
import { useTranslation } from 'react-i18next';
import { COINS_DISABLED } from '../config/coinsFlag';
import WasslaImage from './common/WasslaImage';
import MediaCarousel from './common/MediaCarousel';
import './ProductGrid.css';

const BADGE_I18N = {
  trusted_seller: 'selltrus.trustedSeller',
  verified_seller: 'selltrus.verifiedSeller',
  active_seller: 'selltrus.activeSeller',
};

/**
 * ProductCard — lean, RTL-safe card for marketplace grids.
 * Renders: image, title (2-line clamp), price, optional location, favorite button.
 */
export const ProductCard = memo(function ProductCard({
  product,
  imageUrl,
  title,
  price,
  location,
  isFavorite,
  onOpen,
  onToggleFavorite,
  onImageError,
  onImageLoad,
  badgeText,
  testId,
  children,
}) {
  // Centralized i18n — badges must never be hardcoded (Arabic shoppers were
  // seeing "NEW" in English because this component rolled its own label).
  // Every badge text below flows through t() so switching language updates
  // the whole grid instantly.
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');

  return (
    <article
      className="marketCard"
      data-testid={testId}
      onClick={() => onOpen(product)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(product)}
    >
      <div className="imgWrap">
        {/* iter330 — MediaCarousel handles 0 / 1 / many images:
            • 0 images → Package placeholder
            • 1 image  → plain WasslaImage (no carousel chrome)
            • 2+ images → swipeable carousel with index pill + dots
            WasslaImage owns the per-slide loading / loaded / error
            pipeline (skeleton + branded W mark + fade-in). Lazy-load
            is inherited from WasslaImage's default `loading="lazy"`
            so non-active slides never block the grid. */}
        {(() => {
          // Coalesce all the legacy image fields the backend uses so
          // a carousel works regardless of which path created the ad.
          const imgArr = (() => {
            if (Array.isArray(product?.images) && product.images.length) return product.images.filter(Boolean);
            if (product?.image_url) return [product.image_url];
            if (imageUrl) return [imageUrl];
            return [];
          })();

          return (
            <MediaCarousel
              images={imgArr}
              title={title}
              adId={product?.ad_id}
              onImageLoad={onImageLoad}
              onImageError={onImageError}
              priorityFirst={false}
            />
          );
        })()}

        {onToggleFavorite && (
          <button
            className={`favoriteBtn ${isFavorite ? 'favActive' : ''}`}
            type="button"
            aria-label="Save"
            data-testid={`favorite-${product.ad_id}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(product.ad_id, e);
            }}
          >
            <Heart size={14} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        )}

        {badgeText && <div className="cardBadge">{badgeText}</div>}
        {/* Boost badge */}
        {!COINS_DISABLED && product.boost_type && product.boost_type !== 'none' && (
          <div className="cardBoostBadge">
            {product.boost_type === 'featured' ? '⭐' : product.boost_type === 'urgent' ? '⚡' : '🔥'}
          </div>
        )}
        {/* Rule-based badges — all strings go through i18n */}
        {(() => {
          const isNew = product.created_at && (Date.now() - new Date(product.created_at).getTime()) < 3 * 86400000;
          const isNegotiable = product.price_negotiable || product.negotiable;
          const isVerifiedSeller = product.seller?.is_verified || product.seller?.isVerified || product.seller?.verification?.status === 'verified';
          // Max 2 badges in the top stack (NEW moved to bottom-end, iter404)
          const badges = [];
          if (isNegotiable) badges.push(<span key="neg" className="cardNegBadge" data-testid="card-badge-negotiable">{t('home.negotiable')}</span>);
          if (isVerifiedSeller && badges.length < 2) badges.push(<span key="vs" className="cardVsBadge">✓</span>);
          return (
            <>
              {badges.length > 0 && <div className="cardBadgeStack">{badges}</div>}
              {/* NEW — pinned bottom-right of the image, away from the
                  image-count pill (top-start), heart (top-end) and dots
                  (bottom-center). In RTL, the contextual badge (Ladies Hub)
                  sits physically bottom-right, so NEW shifts to the free
                  bottom-left corner only in that case. */}
              {isNew && (
                <span
                  className={`cardNewBadge cardNewBadgeFloat ${isRTL && badgeText ? 'cardNewBadgeFloat--altSide' : ''}`}
                  data-testid="card-badge-new"
                >
                  {t('home.new')}
                </span>
              )}
            </>
          );
        })()}
      </div>

      <div className="cardBody">
        <h3 className="cardTitle">{title || 'Untitled'}</h3>
        {/* City + time ago row */}
        {(() => {
          const postedAgo = formatRelativePostedTime(product?.created_at, isRTL ? 'ar' : 'en');
          if (!location && !postedAgo) return null;
          return (
            <p className="cardMeta">
              {location && <span>{location}</span>}
              {location && postedAgo && <span> · </span>}
              {postedAgo && <span>{postedAgo}</span>}
            </p>
          );
        })()}
        {/* Verified seller label */}
        {(() => {
          const trustLabel = getSellerTrustLabel(product, isRTL);
          if (!trustLabel) return null;
          return <p className="cardTrustLabel">{trustLabel}</p>;
        })()}
        {price && <p className="cardPrice">{price}</p>}
        {/* iter328 — Seller-provided delivery availability pill.
            Small soft-green badge so buyers see "Delivery" at a
            glance without overcrowding the card. */}
        {product.delivery_available && (
          <span
            className="cardDeliveryBadge"
            data-testid="card-delivery-badge"
          >
            <Truck size={10} />
            {isRTL ? 'توصيل' : 'Delivery'}
          </span>
        )}
        {/* Seller badge from backend trust system */}
        <SellerBadgeTag badge={product.seller?.badge} />
        {/* Price Dropped badge */}
        {product.price_dropped && product.previous_price && (
          <PriceDropBadge previousPrice={product.previous_price} currency={product.currency} />
        )}
        {children}
      </div>
    </article>
  );
});

/**
 * PriceDropBadge — shows previous price and "Price dropped" label.
 */
function PriceDropBadge({ previousPrice, currency }) {
  const { t } = useTranslation();
  const isRTL = document.documentElement.lang === 'ar';
  const formatted = new Intl.NumberFormat(isRTL ? 'ar-SD' : 'en', {
    style: 'decimal',
    maximumFractionDigits: 0,
  }).format(previousPrice);
  return (
    <div className="cardPriceDrop" data-testid="card-price-drop">
      <TrendingDown size={12} />
      <span className="cardOldPrice">{currency || 'SDG'} {formatted}</span>
      <span className="cardDropLabel">{t('priceDrop.badge')}</span>
    </div>
  );
}

/**
 * ProductGrid — responsive 2/3/4-column grid wrapper.
 * Pass dir for RTL or rely on document direction.
 */
export function ProductGrid({ children, dir, className = '' }) {
  return (
    <section className={`marketGrid ${className}`} dir={dir} data-testid="product-grid">
      {children}
    </section>
  );
}

/**
 * SellerBadgeTag — subtle inline badge from backend trust system.
 */
function SellerBadgeTag({ badge }) {
  const { t } = useTranslation();
  const key = BADGE_I18N[badge];
  if (!key) return null;
  const variant = badge === 'trusted_seller' ? 'trusted' : badge === 'verified_seller' ? 'verified' : 'active';
  return (
    <span className={`cardSellerBadge cardSellerBadge--${variant}`} data-testid="seller-badge">
      <ShieldCheck size={10} />
      {t(key)}
    </span>
  );
}
